import React from 'react';

import { Autocomplete, Checkbox, MenuItem, Stack, TableCell, TableRow, TextField, Typography } from '@mui/material';

import { egsRoomName } from '../tabs/egs/egsModel';
import { isOtherBedDescribed } from '../tabs/egs/egsValidation';
import { BED_CHOICES, type BedChoice, type EgsRow } from '../tabs/egs/previousEgs';
import type { RoomAssetLink } from '../tabs/packageOptions/types';

export type EgsRoomsChange = (key: string, rooms: Array<string>) => void;

export type EgsBedChange = (key: string, bed: BedChoice) => void;

export type EgsOtherBedChange = (key: string, description: string) => void;

export type EgsSkipChange = (key: string, skipped: boolean) => void;

type CopyEgsRowProps = {
  allRooms: Array<RoomAssetLink>;
  bed: BedChoice;
  onBedChange: EgsBedChange;
  onOtherBedChange: EgsOtherBedChange;
  onRoomsChange: EgsRoomsChange;
  onSkipChange: EgsSkipChange;
  otherBed: string;
  row: EgsRow;
  rooms: Array<string>;
  skipped: boolean;
};

export default function CopyEgsRow({
  allRooms,
  bed,
  onBedChange,
  onOtherBedChange,
  onRoomsChange,
  onSkipChange,
  otherBed,
  row,
  rooms,
  skipped,
}: CopyEgsRowProps) {
  const missingRooms = !skipped && !rooms.length;
  const missingOtherBed = !skipped && !isOtherBedDescribed(bed, otherBed);

  function handleRoomsChange(_event: React.SyntheticEvent, value: Array<string>) {
    onRoomsChange(row.key, value);
  }

  function handleBedChange(event: React.ChangeEvent<HTMLInputElement>) {
    onBedChange(row.key, BED_CHOICES.find((choice) => choice === event.target.value) ?? '');
  }

  function handleOtherBedChange(event: React.ChangeEvent<HTMLInputElement>) {
    onOtherBedChange(row.key, event.target.value);
  }

  function handleSkipChange(event: React.ChangeEvent<HTMLInputElement>) {
    onSkipChange(row.key, event.target.checked);
  }

  return (
    <TableRow>
      <TableCell>{row.name || 'No name'}</TableCell>
      <TableCell>{row.guestTypes.join(', ')}</TableCell>
      <TableCell sx={{ minWidth: 280 }}>
        <Autocomplete
          disableCloseOnSelect
          disabled={skipped}
          multiple
          onChange={handleRoomsChange}
          getOptionKey={(roomId) => roomId}
          getOptionLabel={(roomId) => egsRoomName(allRooms, roomId)}
          options={allRooms.map((room) => room.id)}
          renderInput={(params) => (
            <TextField
              {...params}
              error={missingRooms}
              helperText={missingRooms ? 'Pick at least one room' : undefined}
              placeholder={rooms.length ? undefined : 'Pick rooms'}
            />
          )}
          size="small"
          value={rooms}
        />
      </TableCell>
      <TableCell sx={{ minWidth: 200 }}>
        {row.bedToChoose ? (
          <Stack spacing={1}>
            <TextField
              SelectProps={{ displayEmpty: true }}
              disabled={skipped}
              fullWidth
              onChange={handleBedChange}
              select
              size="small"
              value={bed}
            >
              <MenuItem value="">
                <Typography color="text.secondary" component="span" variant="body2">
                  Later, on the EGS tab
                </Typography>
              </MenuItem>
              {BED_CHOICES.map((choice) => (
                <MenuItem key={choice} value={choice}>
                  {choice}
                </MenuItem>
              ))}
            </TextField>
            {bed === 'Other' && (
              <TextField
                disabled={skipped}
                error={missingOtherBed}
                fullWidth
                helperText={missingOtherBed ? 'Describe the extra bed' : undefined}
                onChange={handleOtherBedChange}
                placeholder="Other Extra Bedding Type"
                size="small"
                value={otherBed}
              />
            )}
          </Stack>
        ) : (
          <Typography variant="body2">{row.beddings.join(', ')}</Typography>
        )}
      </TableCell>
      <TableCell padding="checkbox">
        <Checkbox
          checked={skipped}
          inputProps={{ 'aria-label': `Skip ${row.name}` }}
          onChange={handleSkipChange}
          size="small"
        />
      </TableCell>
    </TableRow>
  );
}
